// ═══════════════════════════════════════════════════════════════
// PACE — Call-Downs
// Initiate, track, and manage call-down drills
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Title, Text, Group, Stack, Button, TextInput, Select, Textarea,
  Paper, SimpleGrid, Avatar, Modal, Loader, Center, ActionIcon, Progress,
  Badge,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { IconPlus, IconPhone, IconWand, IconBookmark, IconTrash } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore } from '../../store/paceStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { CallDown } from '../../store/paceStore';

const STATUS_CONFIG: Record<string, { label: string; color: string; badgeColor: string }> = {
  pending:      { label: 'Pending',      color: '#627d98',  badgeColor: '#939598' },
  reached:      { label: 'Reached',      color: '#16a34a',  badgeColor: 'var(--color-success)' },
  unreachable:  { label: 'Unreachable',  color: '#B12525',  badgeColor: 'var(--color-error)' },
  acknowledged: { label: 'Acknowledged', color: '#2563eb',  badgeColor: 'var(--color-primary-700)' },
};

export default function CallDowns() {
  const { canEdit } = useModulePermission('pace');
  const { activeIncident } = useAuthStore();
  const {
    callDowns, callDownResponses, callDownTemplates, loading,
    departments: deptList,
    loadCallDowns, loadResponses, loadTemplates, loadDepartments, initiateCallDown,
    closeCallDown, updateResponse, createTemplate, deleteTemplate,
    generateCallDownScript,
  } = usePaceStore();

  const departments = useMemo(
    () => deptList.map(d => ({ value: d, label: d })),
    [deptList],
  );

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ scenario: '', scope: 'org', scopeId: '', script: '' });
  const [generatingScript, setGeneratingScript] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCd, setSelectedCd] = useState<CallDown | null>(null);
  const [updatingResponse, setUpdatingResponse] = useState<number | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const incidentId = activeIncident ? String(activeIncident.id) : '';

  useEffect(() => {
    if (incidentId) loadCallDowns(incidentId);
    loadTemplates();
    loadDepartments();
  }, [incidentId]);

  useEffect(() => {
    if (selectedCd) loadResponses(String(selectedCd.id));
  }, [selectedCd?.id]);

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="xs">
          <IconPhone size={40} color="#d1d5db" />
          <Title order={3}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Select an incident to manage call-downs.</Text>
        </Stack>
      </Center>
    );
  }

  const handleGenScript = async () => {
    if (!form.scenario) return;
    setGeneratingScript(true);
    try {
      const script = await generateCallDownScript(form.scenario, form.scope);
      setForm(f => ({ ...f, script }));
    } catch { notifications.show({ message: 'Script generation failed', color: 'var(--color-error)' }); }
    finally { setGeneratingScript(false); }
  };

  const handleInitiate = async () => {
    if (!form.scenario) return;
    setSubmitting(true);
    try {
      await initiateCallDown({
        incidentId,
        scenario: form.scenario,
        scope: form.scope,
        scopeId: form.scopeId,
        script: form.script,
      });
      notifications.show({ message: 'Call-down initiated', color: 'var(--color-success)' });
      setShowNew(false);
      setForm({ scenario: '', scope: 'org', scopeId: '', script: '' });
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to initiate', color: 'var(--color-error)' });
    } finally { setSubmitting(false); }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !form.scenario) return;
    setSavingTemplate(true);
    try {
      await createTemplate({
        templateName: templateName.trim(),
        scenario: form.scenario,
        scope: form.scope,
        script: form.script,
      });
      notifications.show({ message: 'Template saved', color: 'var(--color-success)' });
      setShowSaveTemplate(false);
      setTemplateName('');
    } catch { notifications.show({ message: 'Failed to save template', color: 'var(--color-error)' }); }
    finally { setSavingTemplate(false); }
  };

  const handleUpdateResponse = useCallback(async (rid: number, status: string) => {
    setUpdatingResponse(rid);
    try {
      await updateResponse(rid, {
        responseStatus: status,
        respondedAt: new Date().toISOString(),
        callDownId: selectedCd?.id,
      });
      if (selectedCd) await loadResponses(String(selectedCd.id));
    } catch { notifications.show({ message: 'Failed to update', color: 'var(--color-error)' }); }
    finally { setUpdatingResponse(null); }
  }, [selectedCd, updateResponse, loadResponses]);

  const pendingCount = callDownResponses.filter(r => r.responseStatus === 'pending').length;
  const reachedCount = callDownResponses.filter(r => ['reached', 'acknowledged'].includes(r.responseStatus)).length;

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Group gap={8}>
          <Title order={3} c="black">Call-Downs</Title>
        </Group>
        {canEdit && <Button leftSection={<IconPlus size={14} />} onClick={() => setShowNew(true)}>
          Initiate Call-Down
        </Button>}
      </Group>

      {/* History */}
      {loading ? (
        <Center py="xl" mih={200}><Loader /></Center>
      ) : callDowns.length === 0 ? (
        <Center py="xl" mih={200}>
          <Stack align="center" gap="xs">
            <IconPhone size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text c="dimmed">No call-downs initiated yet.</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="sm">
          {callDowns.map(cd => {
            const pct = cd.memberCount > 0 ? Math.round((cd.respondedCount / cd.memberCount) * 100) : 0;
            return (
              <Paper key={cd.id} p="md" withBorder style={{ cursor: 'pointer' }}
                onClick={() => setSelectedCd(cd)}>
                <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} truncate="end">{cd.scenario}</Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      {cd.scope === 'org' ? 'Full org' : 'Department'} &middot;{' '}
                      {cd.datecreated ? formatDistanceToNow(new Date(Number(cd.datecreated) * 1000 || cd.datecreated), { addSuffix: true }) : ''}
                    </Text>
                  </Box>
                  <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Box ta="right">
                      <Text size="sm" fw={600}>{cd.respondedCount}/{cd.memberCount}</Text>
                      <Text size="xs" c="dimmed">{pct}% reached</Text>
                    </Box>
                    <Badge color={cd.callStatus === 'active' ? 'var(--color-primary-700)' : 'var(--color-success)'} size="xs">
                      {cd.callStatus}
                    </Badge>
                  </Group>
                </Group>
                <Progress value={pct} color={pct === 100 ? 'var(--color-success)' : 'var(--color-primary-700)'} size="sm" mt="sm" radius="xl" />
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* New Call-Down Modal */}
      <Modal opened={showNew} onClose={() => setShowNew(false)} title="Initiate Call-Down" size="lg">
        <Stack gap="md">
          {callDownTemplates.length > 0 && (
            <Box>
              <Button variant="subtle" size="xs" leftSection={<IconBookmark size={11} />}
                onClick={() => setShowTemplates(!showTemplates)}>
                Load from template ({callDownTemplates.length})
              </Button>
              {showTemplates && (
                <Paper withBorder shadow="md" mt="xs" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {callDownTemplates.map(t => (
                    <Group key={t.id} px="sm" py="xs" justify="space-between" wrap="nowrap"
                      style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Text size="sm" fw={500} truncate="end" style={{ cursor: 'pointer', flex: 1 }}
                        onClick={() => {
                          setForm({ scenario: t.scenario, scope: t.scope, scopeId: '', script: t.script });
                          setShowTemplates(false);
                        }}>
                        {t.templateName}
                      </Text>
                      <ActionIcon variant="subtle" color="#939598" size="xs"
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}>
                        <IconTrash size={10} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Paper>
              )}
            </Box>
          )}

          <Textarea label="Scenario / Reason *" value={form.scenario}
            onChange={e => { const v = e.target.value; setForm(f => ({ ...f, scenario: v })); }}
            placeholder="e.g. Earthquake M6.2 — activate EOC, report availability status" rows={3} />

          <Select label="Scope"
            data={[{ value: 'org', label: 'Full Organization' }, { value: 'department', label: 'Department' }]}
            value={form.scope}
            onChange={(val) => setForm(f => ({ ...f, scope: val || 'org', scopeId: '' }))} />

          {form.scope === 'department' && (
            <Select
              label="Department *"
              placeholder="Select a department..."
              data={departments}
              value={form.scopeId}
              onChange={(val) => setForm(f => ({ ...f, scopeId: val || '' }))}
              searchable
              nothingFoundMessage="No departments found — add departments in PACE profiles"
            />
          )}

          <Box>
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={500}>Call-Down Script</Text>
              <Button variant="subtle" size="xs" color="var(--data-viz-650)" leftSection={<IconWand size={11} />}
                onClick={handleGenScript} disabled={generatingScript || !form.scenario} loading={generatingScript}>
                AI Generate
              </Button>
            </Group>
            <Textarea value={form.script}
              onChange={e => { const v = e.target.value; setForm(f => ({ ...f, script: v })); }}
              placeholder="Script for callers to read. AI can generate one based on your scenario." rows={5}
              styles={{ input: { fontFamily: 'monospace' } }} />
          </Box>

          <Group>
            <Button flex={1} onClick={handleInitiate} disabled={submitting || !form.scenario || (form.scope === 'department' && !form.scopeId)} loading={submitting}>
              Initiate Call-Down
            </Button>
            <Button variant="default" onClick={() => setShowNew(false)}>Cancel</Button>
          </Group>

          {form.scenario && (
            <Box pt="xs" style={{ borderTop: '1px solid #e2e8f0' }}>
              {showSaveTemplate ? (
                <Group gap="xs">
                  <TextInput value={templateName} onChange={e => setTemplateName(e.target.value)}
                    placeholder="Template name..." style={{ flex: 1 }} size="xs" />
                  <Button size="xs" color="var(--color-success)" onClick={handleSaveTemplate}
                    disabled={savingTemplate || !templateName.trim()} loading={savingTemplate}>Save</Button>
                  <Button size="xs" variant="subtle" color="#939598" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
                </Group>
              ) : (
                <Button variant="subtle" size="xs" color="#939598" leftSection={<IconBookmark size={10} />}
                  onClick={() => setShowSaveTemplate(true)}>
                  Save as template
                </Button>
              )}
            </Box>
          )}
        </Stack>
      </Modal>

      {/* Call-Down Detail Modal */}
      <Modal opened={!!selectedCd} onClose={() => setSelectedCd(null)}
        title={selectedCd ? <Text fw={700}>{selectedCd.scenario}</Text> : null} size="lg">
        {selectedCd && (
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {[
                { label: 'Total', value: selectedCd.memberCount, color: '#939598' },
                { label: 'Reached', value: reachedCount, color: 'var(--color-success)' },
                { label: 'Pending', value: pendingCount, color: '#C68700' },
              ].map(({ label, value, color }) => (
                <Paper key={label} p="xs" style={{ background: '#F8F8F8', textAlign: 'center', borderRadius: '4px' }}>
                  <Text size="xl" fw={700} c={color === '#939598' ? undefined : color}>{value}</Text>
                  <Text size="xs" c="dimmed">{label}</Text>
                </Paper>
              ))}
            </SimpleGrid>

            <Progress
              value={selectedCd.memberCount > 0 ? Math.round((reachedCount / selectedCd.memberCount) * 100) : 0}
              color="var(--color-success)" size="md" radius="xl" />

            {selectedCd.script && (
              <details>
                <summary style={{ fontSize: '12px', fontWeight: 500, color: '#475569', cursor: 'pointer' }}>
                  View Script
                </summary>
                <Paper p="sm" mt="xs" style={{ background: '#F8F8F8', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  <Text size="sm">{selectedCd.script}</Text>
                </Paper>
              </details>
            )}

            {/* Response list */}
            {callDownResponses.length === 0 ? (
              <Center py="xl" mih={120}>
                <Text size="sm" c="dimmed">No response records found.</Text>
              </Center>
            ) : (
              <Stack gap={0}>
                {callDownResponses.map(r => {
                  const cfg = STATUS_CONFIG[r.responseStatus] || STATUS_CONFIG.pending;
                  return (
                    <Group key={r.id} px="sm" py="xs" gap="sm" wrap="nowrap"
                      style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Avatar size={32} radius="xl" color={cfg.badgeColor}>
                        {r.personName?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate="end">{r.personName}</Text>
                        <Badge size="xs" color={cfg.badgeColor} variant="light">{cfg.label}</Badge>
                      </Box>
                      {selectedCd.callStatus === 'active' && r.responseStatus === 'pending' && (
                        <Group gap="xs" style={{ flexShrink: 0 }}>
                          <Button size="compact-xs" variant="light" color="var(--color-success)"
                            onClick={() => handleUpdateResponse(r.id, 'reached')}
                            disabled={updatingResponse === r.id} loading={updatingResponse === r.id}>
                            Reached
                          </Button>
                          <Button size="compact-xs" variant="light" color="var(--color-error)"
                            onClick={() => handleUpdateResponse(r.id, 'unreachable')}
                            disabled={updatingResponse === r.id}>
                            Unreachable
                          </Button>
                        </Group>
                      )}
                    </Group>
                  );
                })}
              </Stack>
            )}

            <Group pt="xs" style={{ borderTop: '1px solid #e2e8f0' }}>
              {selectedCd.callStatus === 'active' && (
                <Button color="var(--color-success)" flex={1} onClick={async () => {
                  await closeCallDown(selectedCd.id);
                  notifications.show({ message: 'Call-down completed', color: 'var(--color-success)' });
                  setSelectedCd(null);
                  loadCallDowns(incidentId);
                }}>
                  Mark Complete
                </Button>
              )}
              <Button variant="default" onClick={() => setSelectedCd(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
